import { useMemo } from "react";
import { graphql } from "@acto/gql";
import { TodoStatus } from "@acto/gql/graphql";
import { Todo } from "@acto/taskpane/core/domain";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";

const NoteTodosQuery = graphql(`
  query GetNoteTodos($filter: NoteTodosFilter, $first: Int!, $after: String) {
    noteTodos(filter: $filter, first: $first, after: $after) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          title
          description
          dueDate
          processedStatus
          processedReason
          isWorkflow
          isNoteBased
          client {
            id
            legacyId
            __typename
            name
            externalReference
          }
          assignedUser {
            id
            name
            __typename
            employee {
              id
              name
              __typename
            }
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface UseNoteTodosParams {
  noteId: string;
  processedStatuses?: TodoStatus[];
  limit?: number;
  enabled?: boolean;
}

export function useNoteTodos({
  noteId,
  processedStatuses,
  limit = 10,
  enabled = true,
}: UseNoteTodosParams): PaginatedDataAccessResult<Todo> {
  const filter = useMemo(() => ({ noteId, processedStatuses }), [noteId, processedStatuses]);

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: NoteTodosQuery,
    variables: { filter },
    limit,
    enabled: enabled && !!noteId,
    connection: (data) => data.noteTodos,
  });

  const todos = useMemo(() => data?.map(Todo.fromGraphql), [data]);

  return {
    data: todos,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
